import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTonAddress, useTonConnectUI } from '@tonconnect/ui-react';
import { useSand, type SandPool } from '../hooks/useSand';
import {
  SAND_CONTRACT_ADDRESS,
  SAND_PRMD_MASTER,
  buildGramBet,
  buildJettonBet,
  fetchMyJettonWallet,
  fetchMySandBets,
  parseJettonAmount,
  type SandBetHistoryItem,
} from '../ton/sand';
import { sanitizeReferrerKey } from '../ton/referral';
import { resolveOutgoingReferrerKey } from '../ton/notify';
import { ExplorerLink } from './ExplorerLink';
import { SWAP_ENABLED, explorerTx } from '../ton/config';
import { gaMoneyEvent } from '../analytics';

const SandSwapSheet = lazy(() => import('./SandSwapSheet'));

// Truncates toward zero: a balance must never display above what the wallet
// actually holds, or a "bet it all" ends in an insufficient-funds rejection.
const fmt = (raw: bigint, decimals: number) => {
  const v = Number(raw) / 10 ** decimals;
  return v >= 1000
    ? Math.floor(v).toLocaleString('en')
    : (Math.floor(v * 100) / 100).toLocaleString('en', { maximumFractionDigits: 2 });
};

const mmss = (s: number) =>
  `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const nowSec = () => Math.floor(Date.now() / 1000);

function useNowSec() {
  const [now, setNow] = useState(nowSec);
  useEffect(() => {
    const id = setInterval(() => setNow(nowSec()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function CoinIcon({ pool }: { pool: SandPool }) {
  return (
    <div className="sand-coin__icon">
      {pool.image ? (
        <img src={pool.image} alt="" loading="lazy" />
      ) : (
        (pool.symbol || '?').slice(0, 3)
      )}
    </div>
  );
}

// Each token is a coin inside a status ring:
//  · pulsing gold ring — round finished, the pool awaits its draw (sorted first);
//  · shrinking gold countdown ring — a round is running;
//  · gray ring — the table is empty, a bet opens a round.
// All table details and the bet form live in the sheet opened on tap.
function Coin({
  pool,
  now,
  roundSeconds,
  onOpen,
  prizeWaits,
}: {
  pool: SandPool;
  now: number;
  roundSeconds: number;
  onOpen: (key: string) => void;
  prizeWaits: string;
}) {
  const left = pool.live ? pool.endTime - now : -1;
  const hot = pool.live && left <= 0;
  const R = 38;
  const CIRC = 2 * Math.PI * R;
  const frac = pool.live && !hot
    ? Math.max(0, Math.min(1, left / roundSeconds))
    : 0;
  const cls = hot ? 'hot' : pool.live ? 'live' : 'idle';
  return (
    <button
      type="button"
      className={`sand-coin sand-coin--${cls}`}
      onClick={() => onOpen(pool.key)}
    >
      <div className="sand-coin__wrap">
        <svg width="82" height="82" viewBox="0 0 82 82">
          {hot ? (
            <circle className="sand-coin__rhot" cx="41" cy="41" r={R} strokeWidth="4" />
          ) : (
            <>
              <circle className="sand-coin__rbase" cx="41" cy="41" r={R} strokeWidth="4" />
              {pool.live && (
                <circle
                  className="sand-coin__rarc"
                  cx="41"
                  cy="41"
                  r={R}
                  strokeWidth="4"
                  strokeDasharray={CIRC}
                  strokeDashoffset={CIRC * (1 - frac)}
                />
              )}
            </>
          )}
        </svg>
        <CoinIcon pool={pool} />
      </div>
      <div className="sand-coin__sym">{pool.symbol}</div>
      <div className="sand-coin__meta">
        {hot
          ? `${fmt(pool.totalPool, pool.decimals)} · ${prizeWaits}`
          : pool.live
            ? mmss(Math.max(0, left))
            : ''}
      </div>
    </button>
  );
}

interface SheetProps {
  pool: SandPool;
  roundSeconds: number;
  holdings: ReturnType<typeof useSand>['holdings'];
  onClose: () => void;
  onNotice: (msg: string, kind: 'ok' | 'err') => void;
  onBetSent: () => void;
  /** Opens the in-app GRAM → token swap; absent when the swap is unavailable. */
  onBuyToken?: (() => void) | null;
  /** The inviter's numeric invite key to attach to the bet, if any. */
  refKey: number | null;
}

function BetSheet({ pool: p, roundSeconds, holdings, onClose, onNotice, onBetSent, onBuyToken, refKey }: SheetProps) {
  const { t } = useTranslation();
  const address = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const now = useNowSec();

  const my = p.master ? holdings.jettons.get(p.master) : null;
  const left = p.live ? p.endTime - now : -1;
  const hot = p.live && left <= 0;

  // Minimum bet: GRAM — 0.5 (the contract accepts 0.25, but a GRAM bet's
  // winner-scan weight is ~0.9×(bet − overhead) − 0.25: the fixed ref-resolve
  // budget leaves with the bet and only its change returns — to the round's
  // carry (prize), never to the bet's weight. Below ~0.33 the weight is zero,
  // so the UI floor keeps every bet's chance meaningful); jettons — 100 base
  // units (two orders above decimals so the 5% burn divides without a
  // remainder).
  const minBet = p.isTon ? 0.5 : 100 / 10 ** p.decimals;
  const minStr = p.isTon
    ? '0.5'
    : minBet
        .toFixed(Math.max(0, p.decimals - 2))
        .replace(/(\.\d*?)0+$/, '$1')
        .replace(/\.$/, '');
  const sliderMin = minBet;
  let sliderMax = 0;
  if (p.isTon && holdings.tonBalance > 0n) {
    sliderMax = Math.max(0, Number(holdings.tonBalance) / 1e9 - 0.05);
  } else if (!p.isTon && my) {
    sliderMax = Number(my.balance) / 10 ** my.decimals;
  }

  const [amount, setAmount] = useState(minStr);
  const [slider, setSlider] = useState(0);
  const [belowMin, setBelowMin] = useState(false);
  const [pending, setPending] = useState(false);
  const minTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fmtV = (v: number) =>
    v >= 100
      ? String(Math.floor(v))
      : v >= 1
        ? String(Math.floor(v * 100) / 100)
        : String(Math.floor(v * 10000) / 10000);

  const parsed = parseFloat((amount || '').replace(',', '.'));

  // Entering less than the minimum flashes red, shakes and snaps back.
  const enforceMin = () => {
    const v = parseFloat((amount || '').replace(',', '.'));
    if (!Number.isFinite(v) || v >= minBet) return;
    setBelowMin(true);
    setTimeout(() => {
      setBelowMin(false);
      setAmount(minStr);
    }, 450);
  };

  const onAmountChange = (value: string) => {
    setAmount(value);
    const v = parseFloat((value || '').replace(',', '.'));
    if (v >= sliderMin && v <= sliderMax && sliderMax > sliderMin) {
      setSlider(Math.round(((v - sliderMin) / (sliderMax - sliderMin)) * 1000));
    }
    if (minTimer.current) clearTimeout(minTimer.current);
    minTimer.current = setTimeout(enforceMin, 900);
  };

  const onSliderChange = (value: number) => {
    setSlider(value);
    const v = sliderMin + (sliderMax - sliderMin) * (value / 1000);
    const s = fmtV(v);
    setAmount(parseFloat(s) >= minBet ? s : minStr);
  };

  const chance = useMemo(() => {
    const v = parsed;
    if (!v || v <= 0) return null;
    // Contract mechanics: jettons burn 5% on entry (95% joins the pool, the
    // winner takes the whole pool). A GRAM bet's recorded winner-scan weight
    // is what survives the deductions, in order: the adaptive gas deduction
    // (~0.01) and the 0.03 draw-reserve tithe, then 5% owner + 5% referral
    // pot, then the fixed ref-resolve budget min(0.25, remainder), which
    // physically leaves with the resolve request — its change returns to the
    // round's carry (prize only, never weight).
    const GRAM_BET_OVERHEAD = 0.04; // ~0.03 reserve tithe + ~0.01 gas
    const GRAM_RESOLVE_VALUE = 0.25; // SAND_REF_RESOLVE_VALUE
    const mine =
      10 ** p.decimals *
      (p.isTon
        ? Math.max(0, (v - GRAM_BET_OVERHEAD) * 0.9 - GRAM_RESOLVE_VALUE)
        : v * 0.95);
    if (mine <= 0) return null;
    if (p.live && Number(p.totalPool) > 0) {
      // The winner scan weighs only the recorded bets: the carry (past
      // rounds' change) is prize-only, so it inflates the take but not the
      // denominator of the chance.
      const weightPool = Math.max(0, Number(p.totalPool) - Number(p.carry));
      const ch = Math.min(100, (mine / (weightPool + mine)) * 100);
      const total = Number(p.totalPool) + mine;
      const take = fmt(BigInt(Math.floor(p.isTon ? total * 0.95 : total)), p.decimals);
      return t('sand.chance', { chance: ch.toFixed(1), amount: take, symbol: p.symbol });
    }
    const take = fmt(BigInt(Math.floor(p.isTon ? mine * 0.95 : mine)), p.decimals);
    return t('sand.chanceFirst', { amount: take, symbol: p.symbol });
  }, [parsed, p, t]);

  const bet = async () => {
    const amountStr = (amount || '').trim().replace(',', '.');
    if (!/^\d*\.?\d+$/.test(amountStr)) {
      onNotice(t('sand.errAmount'), 'err');
      return;
    }
    if (!address) {
      onNotice(t('sand.errConnect'), 'err');
      await tonConnectUI.openModal();
      return;
    }
    setPending(true);
    try {
      if (p.isTon) {
        const v = parseFloat(amountStr);
        if (!(v >= minBet)) {
          onNotice(t('sand.errMin', { amount: minStr, symbol: 'GRAM' }), 'err');
          return;
        }
        await tonConnectUI.sendTransaction(buildGramBet(amountStr, refKey));
        gaMoneyEvent('bet_sand', 'GRAM', v);
      } else {
        if (parseFloat(amountStr) < minBet) {
          onNotice(t('sand.errMin', { amount: minStr, symbol: p.symbol }), 'err');
          return;
        }
        let jw = my?.wallet ?? null;
        let decimals = my?.decimals ?? p.decimals;
        if (!jw) {
          if (!p.master) {
            onNotice(t('sand.errUnknownToken'), 'err');
            return;
          }
          const found = await fetchMyJettonWallet(address, p.master);
          if (!found) {
            onNotice(t('sand.errNoToken', { symbol: p.symbol }), 'err');
            return;
          }
          jw = found.wallet;
          decimals = found.decimals;
        }
        const units = parseJettonAmount(amountStr, decimals);
        if (units <= 0n) {
          onNotice(t('sand.errPositive'), 'err');
          return;
        }
        await tonConnectUI.sendTransaction(buildJettonBet(jw, address, units, refKey));
        gaMoneyEvent('bet_sand', p.symbol, parseFloat(amountStr), p.master ?? '');
      }
      onClose();
      onNotice(t('sand.sent', { amount: amountStr, symbol: p.symbol }), 'ok');
      onBetSent();
    } catch (e) {
      onNotice(
        t('sand.errSend', { message: e instanceof Error ? e.message : String(e) }),
        'err',
      );
    } finally {
      setPending(false);
    }
  };

  const bal = p.isTon ? null : my ? fmt(my.balance, my.decimals) : null;

  return (
    <div
      className="sand-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="sand-sheet">
        <div className="sand-sheet__top">
          <CoinIcon pool={p} />
          <h3>
            {p.symbol} —{' '}
            {hot
              ? t('sand.statusHot')
              : p.live
                ? t('sand.statusLive')
                : t('sand.statusNew')}
          </h3>
          <button type="button" className="sand-sheet__close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="sand-sheet__stats">
          <div className="sand-stat">
            <span className="sand-stat__lbl">{t('sand.statPool')}</span>
            <b className="sand-stat__val sand-gold">
              {p.live ? `${fmt(p.totalPool, p.decimals)} ${p.symbol}` : '—'}
            </b>
          </div>
          <div className="sand-stat">
            <span className="sand-stat__lbl">{t('sand.statPlayers')}</span>
            <b className="sand-stat__val">{p.live ? p.betCount : 0}</b>
          </div>
          <div className="sand-stat">
            <span className="sand-stat__lbl">{t('sand.statDraw')}</span>
            <b className="sand-stat__val">
              {hot
                ? t('sand.prizeWaits')
                : p.live
                  ? mmss(Math.max(0, left))
                  : `${Math.round(roundSeconds / 60)}:00`}
            </b>
          </div>
        </div>
        <p className="sand-sheet__cta">
          {hot
            ? t('sand.ctaHot')
            : p.live
              ? t('sand.ctaLive', { time: mmss(Math.max(0, left)) })
              : t('sand.ctaNew')}
        </p>
        {bal && (
          <div className="sand-sheet__bal">
            {t('sand.youHave', { amount: bal, symbol: p.symbol })}
          </div>
        )}
        <input
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className={`sand-sheet__amt ${belowMin ? 'below-min' : ''}`}
          placeholder={
            p.isTon
              ? t('sand.placeholderGram')
              : t('sand.placeholderJetton', { symbol: p.symbol })
          }
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          onBlur={() => {
            if (minTimer.current) clearTimeout(minTimer.current);
            enforceMin();
          }}
        />
        {sliderMax > sliderMin ? (
          <>
            <input
              type="range"
              className="sand-slider"
              min={0}
              max={1000}
              value={slider}
              style={{ ['--fill' as string]: `${(slider / 1000) * 100}%` }}
              onChange={(e) => onSliderChange(Number(e.target.value))}
            />
            <div className="sand-slider__lbls">
              <span>{minStr}</span>
              <span>
                {fmtV(sliderMax)} · {t('sand.allBalance')}
              </span>
            </div>
            <div className="sand-quick">
              {(
                [
                  [minStr, 0],
                  ['25%', 250],
                  ['50%', 500],
                  [t('sand.quickMax'), 1000],
                ] as const
              ).map(([label, value]) => (
                <button
                  type="button"
                  key={label}
                  onClick={() => onSliderChange(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="sand-slider__lbls sand-slider__lbls--hint">
            <span>
              {address
                ? p.isTon
                  ? t('sand.notEnoughGram')
                  : t('sand.errNoToken', { symbol: p.symbol })
                : t('sand.connectForSlider')}
            </span>
          </div>
        )}
        <div className="sand-sheet__chance">{chance}</div>
        <button
          type="button"
          className={`sand-sheet__go ${hot ? 'sand-sheet__go--hot' : ''}`}
          onClick={() => void bet()}
          disabled={pending}
        >
          {hot ? t('sand.betHot') : t('sand.bet')}
        </button>
        {onBuyToken && (
          <button type="button" className="sand-sheet__buy" onClick={onBuyToken}>
            {t('sandSwap.buy', { symbol: p.symbol })}
          </button>
        )}
        <ul className="sand-sheet__mechlist">
          <li>🏆 {t('sand.mechB1')}</li>
          <li>{p.isTon ? `🏦 ${t('sand.mechBGram')}` : `🔥 ${t('sand.mechBJetton')}`}</li>
          <li>⚖️ {t('sand.mechB2')}</li>
        </ul>
      </div>
    </div>
  );
}

/** The wallet's own recent bets with round outcomes, at the page bottom. */
function BetHistory({
  address,
  pools,
  refreshToken,
}: {
  address: string;
  pools: SandPool[];
  refreshToken: number;
}) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<SandBetHistoryItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const bets = await fetchMySandBets(address);
        if (!cancelled) setItems(bets);
      } catch {
        // history is auxiliary — keep whatever we had
      }
    };
    void load();
    const id = setInterval(() => void load(), 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [address, refreshToken]);

  if (items.length === 0) return null;

  const metaOf = (master: string | null): { symbol: string; decimals: number } => {
    if (master === null) return { symbol: 'GRAM', decimals: 9 };
    const p = pools.find((x) => x.master === master);
    return p ? { symbol: p.symbol, decimals: p.decimals } : { symbol: '', decimals: 9 };
  };
  const when = (utime: number) =>
    new Date(utime * 1000).toLocaleString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="sand-history">
      <div className="sand-history__title">{t('sand.historyTitle')}</div>
      {items.map((b) => {
        const meta = metaOf(b.tokenMaster);
        const amount = `${fmt(BigInt(b.amountRaw), meta.decimals)} ${meta.symbol}`;
        const prize =
          b.prizeRaw !== null ? `${fmt(BigInt(b.prizeRaw), meta.decimals)} ${meta.symbol}` : null;
        return (
          <div key={b.txHash} className="sand-history__row">
            <div className="sand-history__main">
              <span className="sand-history__amount">{amount}</span>
              <span className="sand-history__when">{when(b.utime)}</span>
            </div>
            <div className="sand-history__side">
              <span className={`sand-history__status sand-history__status--${b.status}`}>
                {b.status === 'won' && prize
                  ? t('sand.historyWonAmount', { amount: prize })
                  : t(`sand.history_${b.status}`)}
              </span>
              <span className="sand-history__links">
                <ExplorerLink
                  address={b.txHash}
                  href={explorerTx(b.txHash)}
                  label={t('sand.historyBetTx')}
                />
                {b.drawTxHash && (
                  <ExplorerLink
                    address={b.drawTxHash}
                    href={explorerTx(b.drawTxHash)}
                    label={t('sand.historyDrawTx')}
                  />
                )}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function Sand() {
  const { t } = useTranslation();
  const address = useTonAddress();
  const { pools, holdings, roundSeconds, loaded, error, refresh } = useSand(
    address || null,
  );
  const now = useNowSec();
  const [historyToken, setHistoryToken] = useState(0);

  // The inviter's numeric invite key the app was opened with (link / bot
  // deep link) — attached to every bet and claim as-is; the contract
  // resolves it to a wallet on-chain and only honours it on the wallet's
  // first contact, so re-supplying is safe. Key 0 (the owner) is valid.
  const [refKey, setRefKey] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const pyramidaKey = sanitizeReferrerKey(
        await resolveOutgoingReferrerKey(address || null),
      );
      if (pyramidaKey === null) return;
      if (!cancelled) setRefKey(pyramidaKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [address]);

  const [sheetKey, setSheetKey] = useState<string | null>(null);
  const [swapKey, setSwapKey] = useState<string | null>(null);
  // Set when the swap sheet opened because the user lacks the table's token —
  // a settled swap then flows straight into the bet form.
  const [swapNoToken, setSwapNoToken] = useState(false);
  const [notice, setNotice] = useState<{ msg: string; kind: 'ok' | 'err' } | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNotice = (msg: string, kind: 'ok' | 'err') => {
    setNotice({ msg, kind });
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 3500);
  };

  // Coin sections: finished rounds (pool awaiting its draw) → running rounds
  // (nearest draw first) → empty tables. Inside every section PRMD leads,
  // GRAM follows, the rest keep the section's own order.
  const { hot, live, idle } = useMemo(() => {
    // PRMD always heads its section, GRAM is always right behind it.
    const rank = (p: SandPool) =>
      p.master === SAND_PRMD_MASTER ? 0 : p.key === 'gram' ? 1 : 2;
    // Running tables are ordered by the pool's USD value (biggest prize
    // first), falling back to the nearest draw when prices are unavailable.
    const hot = pools
      .filter((p) => p.live && p.endTime <= now)
      .sort(
        (a, b) => rank(a) - rank(b) || b.poolUsd - a.poolUsd || a.endTime - b.endTime,
      );
    const live = pools
      .filter((p) => p.live && p.endTime > now)
      .sort(
        (a, b) => rank(a) - rank(b) || b.poolUsd - a.poolUsd || a.endTime - b.endTime,
      );
    // Empty tables are ordered by potential support: the aggregate USD value
    // players hold in the token (backend wallet snapshots at DEX prices).
    const idle = pools
      .filter((p) => !p.live)
      .sort(
        (a, b) =>
          rank(a) - rank(b) ||
          b.usd - a.usd ||
          a.symbol.localeCompare(b.symbol),
      );
    return { hot, live, idle };
  }, [pools, now]);

  // Spotlight: the single most conversion-ready table — the most valuable
  // ripe pool (one bet triggers the draw), otherwise the most valuable
  // running round.
  const spotlight = useMemo(() => {
    const richest = (list: SandPool[]) =>
      list.length
        ? list.reduce((best, p) => (p.poolUsd > best.poolUsd ? p : best))
        : null;
    return richest(hot) ?? richest(live);
  }, [hot, live]);

  const sheetPool = sheetKey ? pools.find((p) => p.key === sheetKey) ?? null : null;
  const swapPool = swapKey ? pools.find((p) => p.key === swapKey) ?? null : null;

  // Tapping a jetton table the user has no coins for opens the swap right
  // away (destination fixed to the table's token); the bet form comes after.
  const openTable = (key: string) => {
    const p = pools.find((x) => x.key === key);
    if (
      SWAP_ENABLED &&
      p &&
      !p.isTon &&
      p.master &&
      !holdings.jettons.has(p.master) &&
      address
    ) {
      setSwapNoToken(true);
      setSwapKey(key);
      return;
    }
    setSheetKey(key);
  };

  return (
    <section className="sand-page">
      <header className="sand-hero">
        <h2 className="sand-hero__title">Sand</h2>
        <p className="sand-hero__tag">
          {t('sand.heroTag', { minutes: Math.round(roundSeconds / 60) })}
        </p>
        <ol className="sand-hero__steps">
          <li>{t('sand.step1')}</li>
          <li>{t('sand.step2')}</li>
          <li>{t('sand.step3')}</li>
        </ol>
      </header>
      {spotlight && (
        <button
          type="button"
          className={`sand-spot ${spotlight.endTime <= now ? 'sand-spot--hot' : ''}`}
          onClick={() => openTable(spotlight.key)}
        >
          <CoinIcon pool={spotlight} />
          <div className="sand-spot__body">
            <div className="sand-spot__pool">
              {fmt(spotlight.totalPool, spotlight.decimals)}{' '}
              <span>{spotlight.symbol}</span>
            </div>
            <div className="sand-spot__meta">
              {spotlight.endTime <= now
                ? t('sand.statusHot')
                : t('sand.untilDraw', {
                    time: mmss(Math.max(0, spotlight.endTime - now)),
                  })}{' '}
              · {t('sand.players', { count: spotlight.betCount })}
            </div>
          </div>
          <span className="sand-spot__cta">{t('sand.betNow')}</span>
        </button>
      )}
      {error && !loaded ? (
        <div className="sand-empty">{t('sand.errUpdate')}</div>
      ) : pools.length === 0 ? (
        <div className="sand-empty">{t('sand.loading')}</div>
      ) : (
        <>
          {(
            [
              [hot, t('sand.secHot'), 'hot'],
              [live, t('sand.secLive'), 'live'],
              [idle, t('sand.secIdle'), 'idle'],
            ] as const
          ).map(
            ([group, title, key]) =>
              group.length > 0 && (
                <div key={key}>
                  <h3 className="sand-page__title">{title}</h3>
                  <div className="sand-coins">
                    {group.map((p) => (
                      <Coin
                        key={p.key}
                        pool={p}
                        now={now}
                        roundSeconds={roundSeconds}
                        onOpen={openTable}
                        prizeWaits={t('sand.prizeWaits')}
                      />
                    ))}
                  </div>
                </div>
              ),
          )}
        </>
      )}
      {address && (
        <BetHistory address={address} pools={pools} refreshToken={historyToken} />
      )}
      <div className="sand-footnote">
        {t('sand.footnote')}{' '}
        <ExplorerLink address={SAND_CONTRACT_ADDRESS} />
      </div>
      {notice && (
        <div className={`sand-toast sand-toast--${notice.kind}`}>{notice.msg}</div>
      )}
      {sheetPool && !swapPool && (
        <BetSheet
          pool={sheetPool}
          roundSeconds={roundSeconds}
          holdings={holdings}
          onClose={() => setSheetKey(null)}
          onNotice={showNotice}
          onBetSent={() => {
            setTimeout(() => void refresh(true), 12_000);
            setTimeout(() => void refresh(true), 30_000);
            setTimeout(() => setHistoryToken((n) => n + 1), 15_000);
          }}
          onBuyToken={
            SWAP_ENABLED &&
            !sheetPool.isTon &&
            sheetPool.master &&
            !holdings.jettons.has(sheetPool.master)
              ? () => {
                  setSwapNoToken(true);
                  setSwapKey(sheetPool.key);
                  setSheetKey(null);
                }
              : null
          }
          refKey={refKey}
        />
      )}
      {swapPool && swapPool.master && (
        <Suspense fallback={null}>
          <SandSwapSheet
            master={swapPool.master}
            symbol={swapPool.symbol}
            image={swapPool.image}
            decimals={swapPool.decimals}
            holdings={holdings}
            noToken={swapNoToken}
            onClose={() => {
              setSwapKey(null);
              setSwapNoToken(false);
            }}
            onSwapped={() => {
              void refresh(true);
              setTimeout(() => void refresh(true), 5_000);
              // The swap settled — drop the user into the bet form.
              const key = swapPool.key;
              setSwapKey(null);
              setSwapNoToken(false);
              setSheetKey(key);
            }}
          />
        </Suspense>
      )}
    </section>
  );
}
